import { useEffect, useReducer, useRef } from "react";
import {
  Column,
  DatePicker,
  DatePickerInput,
  Row,
  TimePicker,
} from "carbon-components-react";
import { Label } from "../Label";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DateTimeFormat, TimeZone } from "../../../utils/datetime";

const TIME_PATTERN = "([012][0-9])([0-5][0-9])";
const timeRegex = new RegExp(TIME_PATTERN);
const emptyDisplay = { date: "", time: "" };

dayjs.extend(utc);
dayjs.extend(timezone);

export const DateTime = ({
  id,
  className,
  style,
  disabled = false,
  field = {},
  data = "",
  dateFormat = DateTimeFormat.date,
  timeFormat = DateTimeFormat.shortTime,
  timeZone = TimeZone,
  onChange,
}) => {
  dayjs.tz.setDefault(timeZone);

  const { key, required = false } = field;
  const { title } = field.schema;

  const hadData = data !== "" && data !== null;
  const [display, updateDisplay] = useReducer(
    displayReducer,
    hadData ? formatDisplay(data, dateFormat, timeFormat) : emptyDisplay
  );
  const preserveDateOnNullRef = useRef(false);

  useEffect(() => {
    if (hadData) {
      updateDisplay({
        type: "replace",
        display: formatDisplay(data, dateFormat, timeFormat),
      });
      preserveDateOnNullRef.current = false;
      return;
    }

    if (preserveDateOnNullRef.current) {
      preserveDateOnNullRef.current = false;
      updateDisplay({ type: "time", time: "" });
      return;
    }

    updateDisplay({ type: "replace", display: emptyDisplay });
  }, [data, dateFormat, hadData, timeFormat]);

  const handleDateUpdate = ([date = ""]) => {
    if (!date) return;

    if (date === "") {
      if (typeof onChange !== "function") return;
      onChange({ [key]: null });
      return;
    }

    let newDate = dayjs(date);
    const timeMatch = display.time.match(timeRegex);
    if (timeMatch) {
      const [, hour, minute] = timeMatch;
      newDate = newDate.hour(parseInt(hour, 10)).minute(parseInt(minute, 10));
    }

    if (!newDate.isValid()) return;
    updateDisplay({ type: "date", date: newDate.format(dateFormat) });

    if (!timeMatch) return;
    const v = newDate.toISOString();

    if (typeof onChange !== "function") return;
    onChange({ [key]: v });
  };

  const handleTimeUpdate = (input) => {
    const str = input.target.value;
    updateDisplay({ type: "time", time: str });

    if (str === "") {
      preserveDateOnNullRef.current = true;
      if (typeof onChange !== "function") return;
      onChange({ [key]: null });
      return;
    }

    if (str.length < 4) return;

    const match = str.match(timeRegex);
    if (!match) return;
    const [, hour, minute] = match;
    let newDate = display.date ? dayjs(display.date) : dayjs();
    newDate = newDate.hour(parseInt(hour, 10)).minute(parseInt(minute, 10));

    if (!newDate.isValid()) return;
    updateDisplay({
      type: "replace",
      display: {
        date: newDate.format(dateFormat),
        time: newDate.format(timeFormat),
      },
    });
    const v = newDate.toISOString();

    if (typeof onChange !== "function") return;
    onChange({ [key]: v });
  };

  return (
    <Row>
      <Column>
        <DatePicker
          id={`date-picker-${id}`}
          className={className}
          style={style}
          datePickerType="single"
          dateFormat="Y/m/d"
          disabled={disabled}
          value={display.date}
          onChange={handleDateUpdate}
        >
          <DatePickerInput
            id={`${id || "date-picker"}__input`}
            placeholder="yyyy/mm/dd"
            labelText={<Label required={required}>{title}</Label>}
          />
        </DatePicker>
      </Column>
      <Column>
        <TimePicker
          id={`time-picker-${id}`}
          className={className}
          labelText={<Label required={required}>時間</Label>}
          placeholder="HHMM"
          pattern={TIME_PATTERN}
          value={display.time}
          onChange={handleTimeUpdate}
        ></TimePicker>
      </Column>
    </Row>
  );
};

function formatDisplay(data, dateFormat, timeFormat) {
  const value = dayjs(data);
  return {
    date: value.format(dateFormat),
    time: value.format(timeFormat),
  };
}

function displayReducer(state, action) {
  switch (action.type) {
    case "date":
      return { ...state, date: action.date };
    case "time":
      return { ...state, time: action.time };
    case "replace":
      return action.display;
    default:
      return state;
  }
}
